import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { LeasingHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { TabComponentProps } from '@models/interfaces';
import { UserPermissionKeys } from '@models/types';
import { EditablePage } from '@shared/components';
import { LeaseAudit } from '@shared/components/lease-audit/components';
import { ScopingActions } from '@store/actions';
import {
  DDVSelectors,
  ProjectSelectors,
  ScopingSelectors,
  UserSelectors,
} from '@store/selectors';

export const ScopingLeaseAuditTab: FC<
  TabComponentProps & { area: UserPermissionKeys; subPage: SubPage }
> = ({
  isActive,
  projectTypeSelector,
  area,
  subPage,
}: PropsWithChildren<
  TabComponentProps & { area: UserPermissionKeys; subPage: SubPage }
>) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    ScopingSelectors.getTabLoadingState('leaseAudit'),
  );
  const isDataLoaded = useSelector(
    ScopingSelectors.getTabLoadedState('leaseAudit'),
  );

  const { browse } = useSelector(UserSelectors.getLeasingPermissions);
  const projectType = useSelector(projectTypeSelector);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(area, subPage, Tab.LeaseAudit),
  );
  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        ScopingActions.setTabLoading.done({
          tabName: 'leaseAudit',
          isLoading: true,
        }),
      );

      const loadLeaseAuditTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(
            LeasingHttpService,
          ).getLeasingLeaseAuditData(projectBundleID);
          dispatch(
            ScopingActions.setTabLoaded.done({
              tabName: 'leaseAudit',
              isLoaded: true,
            }),
          );
        } catch {
          dispatch(
            ScopingActions.setTabError.done({
              tabName: 'leaseAudit',
              hasError: true,
            }),
          );
        }
      };

      loadLeaseAuditTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-scoping-lease-audit loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <EditablePage
      isActive={isActive}
      editableComponent={LeaseAudit}
      permissions={browse}
      area={area}
      canUserEdit={
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
    />
  );
};
